// *******************************************************************************
// THIS MODULE IS RESPONSIBLE TO MANAGE ALL THE NECESSARY THINGS ABOUT THE REDUX
// *******************************************************************************

import { EnvironmentProviders } from '@angular/core';
import { provideEffects } from '@ngrx/effects';
import { ActionReducerMap, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { environment } from 'src/environments/environment';
import { AppState } from './app.state';
import { PortalReducer } from './reducers/portal.reducer';
import { PortalEffects } from './effects/portal.effects';
import { CatalogReducer } from './reducers/catalog-products.reducer';
import { CatalogEffects } from './effects/catalog-products.effects';
import { UserEventsReducer } from './reducers/user-events.reducer';
import { ApplicationReducer } from './reducers/application.reducer';
import { UserProductsReducer } from './reducers/user-products.reducer';
import { WorkspaceLicensesReducer } from './reducers/workspace-licenses.reducer';
import { UserProductsEffects } from './effects/user-products.effects';
import { WorkspaceLicensesEffects } from './effects/workspace-licenses.effects';
import { ProvisioningContextReducer } from './reducers/provisioning.reducer';
import { UserResourcesReducer } from './reducers/user-resources.reducer';
import { UserResourcesEffects } from './effects/user-resources.effects';
import { ProfileEffects } from './effects/profile.effects';
import { ProfileReducer } from './reducers/profile.reducer';
import { UserPoliciesEffects } from './effects/user-policies.effects';
import { UserPoliciesReducer } from './reducers/user-policies.reducer';
import { UserWorkspacesReducer } from './reducers/user-workspaces.reducer';
import { UserWorkspacesEffects } from './effects/user-workspaces.effects';
import { OrganizationCatalogReducer } from './reducers/organization-catalog.reducer';
import { OrganizationCatalogEffects } from './effects/organization-catalog.effects';
import { MyWorkflowsRequestsEffects } from './effects/workflows/my-workflows-request.effects';
import { MyWorkflowsRequestReducer } from './reducers/workflows/my-workflows-request.reducer';
import { TemplateInstallationRequestsReducer } from './reducers/templates/template-installation-requests.reducer';
import { TemplateInstallationRequestsEffects } from './effects/template-installation-requests.effects';
import { ConsentsPermissionsReducer } from '../modules/workspace/redux/reducers/consents-permissions.reducer';
import { ConsentsPermissionsEffects } from '../modules/workspace/redux/effects/consents-permissions.effects';
import { TenantInformationReducer } from './reducers/tenant/tenant-info.reducer';
import { TenantInfoEffects } from './effects/tenant-info.effects';
import { CheckedOutEntitiesReducer } from './reducers/checked-out-entities.reducer';
import { TokensReducer } from './reducers/tokens.reducer';
import { TokensEffects } from './effects/tokens.effects';

export const disableReduxConsoleExtension = environment.disableReduxConsole;

export const reducers: ActionReducerMap<AppState> = {
	portal: PortalReducer,
	catalog: CatalogReducer,
	userProducts: UserProductsReducer,
	workspaceLicenses: WorkspaceLicensesReducer,
	userEvents: UserEventsReducer,
	userResources: UserResourcesReducer,
	provisioningContext: ProvisioningContextReducer,
	application: ApplicationReducer,
	profile: ProfileReducer,
	userPolicies: UserPoliciesReducer,
	userWorkspaces: UserWorkspacesReducer,
	organizationCatalog: OrganizationCatalogReducer,
	myWorkflowsRequests: MyWorkflowsRequestReducer,
	checkedOutEntities: CheckedOutEntitiesReducer,
	tokens: TokensReducer,
	templateInstallationRequests: TemplateInstallationRequestsReducer,
	userTenantConsentsPermissions: ConsentsPermissionsReducer,
	tenantInfo: TenantInformationReducer
};

const effects = [PortalEffects, CatalogEffects, UserProductsEffects, WorkspaceLicensesEffects, UserResourcesEffects, ProfileEffects, UserPoliciesEffects, UserWorkspacesEffects, OrganizationCatalogEffects, MyWorkflowsRequestsEffects, TemplateInstallationRequestsEffects, ConsentsPermissionsEffects, TenantInfoEffects, TokensEffects];

/** Standalone providers for NgRx root store — compatible with provideState() in lazy routes. */
export function provideAppState(): EnvironmentProviders[] {
	return [
		provideStore(reducers, {
			runtimeChecks: {
				strictStateImmutability: false,
				strictActionImmutability: false
			}
		}),
		provideEffects(effects),
		...(environment.disableReduxConsole ? [] : [
			provideStoreDevtools({ maxAge: 25, logOnly: environment.production, name: 'BindTuning AdminApp Store', connectInZone: true })
		]),
	];
}
